/**
 * Production Reports page — Sprint 0143 PREP-14.
 *
 * 8 can-stock + 14 MIS Excel reports, two tabs, one filter strip,
 * one big button per report. Built for blue-collar usability:
 * no dropdowns of dropdowns, no nested config — pick date, click,
 * Excel downloads.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd'
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import { usePlantId } from '@/shared/hooks/usePlantId'
import {
  downloadReport,
  fetchReportsCatalogue,
  ReportInfo,
  ReportKind,
  ReportParams,
} from '@/features/production/productionReportsApi'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

const fmt = (d: Dayjs | null | undefined) =>
  d ? d.format('YYYY-MM-DD') : undefined

export default function ProductionReportsPage() {
  const { plantId } = usePlantId()
  const [onDate, setOnDate] = useState<Dayjs | null>(dayjs())
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ])
  // Track which button is mid-download — so we can disable + spinner it.
  const [downloading, setDownloading] = useState<string | null>(null)

  const { data: catalogue, isLoading, error } = useQuery({
    queryKey: ['production-reports-catalogue'],
    queryFn: fetchReportsCatalogue,
  })

  const buildParams = (): ReportParams => ({
    plant_id: plantId,
    on_date: fmt(onDate),
    from_date: fmt(range[0]),
    to_date: fmt(range[1]),
  })

  const handleDownload = async (kind: ReportKind, r: ReportInfo) => {
    if (!plantId) {
      message.warning('Please pick a plant first')
      return
    }
    const slot = `${kind}/${r.name}`
    setDownloading(slot)
    try {
      const fname = await downloadReport(kind, r.name, buildParams())
      message.success(`Downloaded ${fname}`)
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (e as any)?.response?.data?.detail || (e as Error).message
      message.error(`Download failed: ${msg}`)
    } finally {
      setDownloading(null)
    }
  }

  const renderReportButtons = (kind: ReportKind, list: ReportInfo[]) => (
    <Row gutter={[16, 16]}>
      {list.map((r) => {
        const slot = `${kind}/${r.name}`
        const busy = downloading === slot
        return (
          <Col key={r.name} xs={24} sm={12} lg={8}>
            <Card
              size="small"
              hoverable
              styles={{ body: { padding: 16 } }}
              onClick={() => !busy && handleDownload(kind, r)}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space>
                  <FileExcelOutlined style={{ color: '#107C41', fontSize: 18 }} />
                  <Text strong>{r.title}</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.description}
                </Text>
                <Button
                  type="primary"
                  size="small"
                  loading={busy}
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownload(kind, r)
                  }}
                  style={{ marginTop: 6 }}
                >
                  {busy ? 'Generating…' : 'Download Excel'}
                </Button>
              </Space>
            </Card>
          </Col>
        )
      })}
    </Row>
  )

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Production Reports
      </Title>
      <Text type="secondary">
        All can-stock and MIS reports as Excel. Pick a date / range below
        then click any report to download.
      </Text>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap size={16}>
          <Space>
            <Text strong>On Date:</Text>
            <DatePicker
              value={onDate}
              onChange={setOnDate}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
          </Space>
          <Space>
            <Text strong>Date Range:</Text>
            <RangePicker
              value={range}
              onChange={(v) => v && setRange(v as [Dayjs, Dayjs])}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
          </Space>
        </Space>
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Some reports use the date, others use the range — each report card uses whichever filter fits."
        />
      </Card>

      {error && (
        <Alert
          type="error"
          message="Failed to load report catalogue"
          description={(error as Error).message}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs
        defaultActiveKey="can-stock"
        items={[
          {
            key: 'can-stock',
            label: `Can Stock (${catalogue?.can_stock?.length ?? 0})`,
            children: catalogue
              ? renderReportButtons('can-stock', catalogue.can_stock)
              : null,
          },
          {
            key: 'mis',
            label: `MIS (${catalogue?.mis?.length ?? 0})`,
            children: catalogue
              ? renderReportButtons('mis', catalogue.mis)
              : null,
          },
        ]}
      />

      {isLoading && (
        <Text type="secondary">Loading report catalogue…</Text>
      )}
    </div>
  )
}
